import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import postal from 'postal';
import ConfirmationModal from '../../../shared/confirmation-modal';
import ImpersonaPromptModal from './publish-wire-components/impersona-prompt-modal';
import AddDisclaimerPromptModal from './publish-wire-components/add-disclaimer-prompt-modal';
import PublishPromptToShareModal from './publish-wire-components/publish-prompt-to-share-modal';
import SchedulePromptToShareModal from './publish-wire-components/schedule-prompt-to-share-modal';
import Loading from '../../../../shared/loading';
import ErrorBoundary from '../../../../shared/error-boundary';
import defaultOnErrorHandler from '../../../../shared/default-on-error-handler';
import {
  getWirePath, updateAutoPublishDate, publish, unpublish,
} from '../../../../services/wire/edit-services';
import { whenAllValid } from '../channel-editor-postal';
import PendingApprovalBadge from './pending-approval-badge';

const edCh = postal.channel('editor');

function getEditPermissions() {
  const profile = window.Livewire?.currentUser?.profile;
  const canEditPublishedWires = profile?.canEditPublishedWires === true;
  const canModifyWiresOfOthers = profile?.canModifyWiresOfOthers === true;
  // Editors with edit permissions can edit wires even when pending approval
  const canEditPendingWire = canModifyWiresOfOthers || canEditPublishedWires;
  return { canEditPendingWire, canModifyWiresOfOthers, canEditPublishedWires };
}

export default class PublishWireControl extends React.Component {
  static propTypes = {
    wireId: PropTypes.number.isRequired,
    validations: PropTypes.arrayOf(PropTypes.string).isRequired,
    autoPublishAt: PropTypes.string,
    wirePublished: PropTypes.bool,
    approvalStatus: PropTypes.string,
    isFeatured: PropTypes.bool,
    categoryValid: PropTypes.bool,
  }

  static defaultProps = {
    autoPublishAt: null,
    wirePublished: false,
    approvalStatus: null,
    isFeatured: false,
    categoryValid: true,
  }

  constructor(props, context) {
    super(props, context);

    this.state = {
      autoPublishAt: this.props.autoPublishAt,
      wirePublished: this.props.wirePublished,
      approvalStatus: this.props.approvalStatus,
      categoryValid: this.props.categoryValid,
    };

    edCh.subscribe('auto-publish-time-changed', d => this.setState({ autoPublishAt: d }));
    edCh.subscribe('field.category.validated', d => this.setState({ categoryValid: d.isValid }));
    edCh.subscribe('category-changed', d => this.setState({ categoryValid: !!d }));
    edCh.subscribe('wire-updated', d => {
      // Use functional setState to handle race conditions with multiple rapid updates
      this.setState(prevState => {
        const updates = {};
        if (d.approvalStatus !== undefined && prevState.approvalStatus !== d.approvalStatus) {
          updates.approvalStatus = d.approvalStatus;
        }
        if (d.published !== undefined && prevState.wirePublished !== d.published) {
          updates.wirePublished = d.published;
        }
        return Object.keys(updates).length > 0 ? updates : null;
      });
    });

    [
      'updateWire',
      'scheduleWire',
      'submit',
      'showAddDisclaimer',
      'proceed',
      'publishWire',
      'unpublishWire',
      'presentUnpublishWireConfirmationModal',
    ].forEach(m => (this[m] = this[m].bind(this)));
  }

  getConfirmModalProps() {
    return {
      title: 'Are you sure you want to unpublish?',
      submitText: 'Unpublish',
      extraClass: '--large',
      onConfirm: this.unpublishWire,
      message: this.props.isFeatured
        ? 'You\'ve featured this wire on your profile. If you unpublish this wire it will be removed from your Feature Wires list on your Profile.'
        : 'When you unpublish a wire it can no longer be discovered or read by investors. You can always republish this wire if you change your mind.',
    };
  }

  getIntent() {
    let ret = 'publish';
    const { canModifyWiresOfOthers, canEditPublishedWires } = getEditPermissions();
    // If the wire is scheduled and not published, return 'schedule'
    if (this.state.autoPublishAt && !this.state.wirePublished) {
      ret = 'schedule';
    // If the wire is published and the user has edit permissions
    // Or the wire is pending approval, return 'update'
    } else if ((this.state.wirePublished && canEditPublishedWires) || (this.state.approvalStatus === 'pending_approval' && !canModifyWiresOfOthers)) {
      ret = 'update';
    }
    return ret;
  }

  getButtonText(intent, requiresWireReview) {
    if (requiresWireReview && !this.state.wirePublished && intent === 'publish') {
      return 'Submit for Review';
    }
    return `${intent} wire`;
  }

  shouldShowReviewHelp(requiresWireReview, isPendingApproval, intent) {
    return requiresWireReview && !this.state.wirePublished && !isPendingApproval && intent === 'publish';
  }

  publishWire() {
    this.loading.start();
    publish(this.props.wireId)
      .then(result => {
        result.data.wireId = this.props.wireId;
        // Publish wire-updated event so this component and other components can update
        // The subscriber (line 53) will handle updating state to avoid race conditions
        edCh.publish('wire-updated', {
          approvalStatus: result.data.approval_status,
          published: result.data.published,
        });
        this.publishedModal.present(result);
      })
      .catch(e => defaultOnErrorHandler(e, 'Error publishing wire'))
      .finally(() => this.loading.stop());
  }

  presentUnpublishWireConfirmationModal() {
    this.unpublishWireConfirmationModal.present();
  }

  unpublishWire() {
    this.loading.start();
    unpublish(this.props.wireId)
      .then(() => {
        this.setState({ wirePublished: false });
        edCh.publish('wire-unpublished');
      })
      .catch(e => defaultOnErrorHandler(e, 'Error unpublishing wire'))
      .finally(() => this.loading.stop());
  }

  scheduleWire() {
    this.loading.start();
    const mpub = moment(this.state.autoPublishAt);
    updateAutoPublishDate(this.props.wireId, mpub.toDate())
      .then(result => {
        result.data.autoPublishAt = mpub;
        result.data.wireId = this.props.wireId;
        if (result.data.approval_status !== undefined) {
          edCh.publish('wire-updated', {
            approvalStatus: result.data.approval_status,
          });
        }
        this.scheduledModal.present(result.data);
      })
      .catch(e => defaultOnErrorHandler(e, 'Error updating schedule settings for wire'))
      .finally(() => this.loading.stop());
  }

  proceed(intention) {
    const func = this[`${intention}Wire`];
    if (typeof func !== 'function') {
      return;
    }
    func();
  }

  showAddDisclaimer(intention) {
    this.addDisclaimerModal.present(intention);
  }

  submit(e) {
    const intention = e.target.getAttribute('data-intent');
    const func = this[`${intention}Wire`];
    if (typeof func !== 'function') {
      return;
    }

    const standfirstLength = document.querySelector('.wire-standfirst-summary textarea');
    if (standfirstLength && standfirstLength.value.length > 140) {
      return;
    }

    whenAllValid({
      fields: this.props.validations,
      action: () => edCh.publish('validate'),
      done: () => this.impersonaModal.present(intention),
    });
  }

  updateWire() {
    this.loading.start();
    getWirePath(this.props.wireId)
      .then(result => {
        window.location = result.data.wire_path;
      })
      .catch(e => defaultOnErrorHandler(e, 'Error getting relative path of the wire'))
      .finally(() => this.loading.stop());
  }

  render() {
    const requiresWireReview = window.Livewire?.currentUser?.profile?.requiresWireReview === 'true' || window.Livewire?.currentUser?.profile?.requiresWireReview === true;
    const isPendingApproval = this.state.approvalStatus === 'pending_approval';
    const canEditPendingWire = getEditPermissions(this.state.approvalStatus);
    const intent = this.getIntent();
    const confirmModalProps = this.getConfirmModalProps();
    const buttonText = this.getButtonText(intent, requiresWireReview);
    const reviewHelpText = 'Your wire will be submitted for editorial review. An editor will review and publish it once approved.';
    return (
      <ErrorBoundary>
        <aside className="sidebar-item publish-button">
          {this.state.categoryValid || <div className="help-block">Please select a category for your wire.</div>}
          {this.shouldShowReviewHelp(requiresWireReview, isPendingApproval, intent) && (
            <div
              className="help-block"
              style={{
                marginBottom: '12px',
                color: '#666',
                fontSize: '14px',
                lineHeight: '1.4',
              }}
            >
              {reviewHelpText}
            </div>
          )}
          {isPendingApproval && !this.state.wirePublished && <PendingApprovalBadge />}
          {(!isPendingApproval || canEditPendingWire) && (
            <Loading variant="inline" ref={el => (this.loading = el)}>
              <button
                type="button"
                className="sidebar-item-primary-button"
                onClick={this.submit}
                data-intent={intent}
              >
                {buttonText}
              </button>
              {
                this.state.wirePublished && (
                <a href="#" className="sidebar-item-secondary-link" onClick={this.presentUnpublishWireConfirmationModal}>
                  Unpublish wire
                </a>
                )
              }
            </Loading>
          )}
        </aside>
        <ImpersonaPromptModal
          ref={el => (this.impersonaModal = el)}
          onDismiss={this.showAddDisclaimer}
        />
        <AddDisclaimerPromptModal
          ref={el => (this.addDisclaimerModal = el)}
          onDismiss={this.proceed}
        />
        <PublishPromptToShareModal ref={el => (this.publishedModal = el)} />
        <SchedulePromptToShareModal ref={el => (this.scheduledModal = el)} />
        <ConfirmationModal
          {...confirmModalProps}
          ref={el => (this.unpublishWireConfirmationModal = el)}
        />
      </ErrorBoundary>
    );
  }
}
